import type { EventItem } from '@/features/publicsite/types/events/EventItem';
import type { EventMarker } from '@/features/publicsite/types/events/EventMarker';
import type { StatusTagVariant } from '@/features/common/types/StatusTagVariant';
import type { PublicEventsKey } from '@/i18n/dictionaries/publicEvents';

/**
 * The states an event can be in on the public calendar — four of the eight the column allows; see
 * EventItem.status for why DRAFT, PENDING, REJECTED and WITHDRAWN are not among them. CANCELLED is:
 * an event people saw and may have registered for stays, marked, rather than vanishing.
 */
export const PUBLIC_EVENT_STATUS = {
  published: 'PUBLISHED',
  concluded: 'CONCLUDED',
  archived: 'ARCHIVED',
  cancelled: 'CANCELLED',
} as const;

export const EVENT_MARKER = { cancelled: 'cancelled', past: 'past' } as const;

/**
 * The one marker a calendar card and an event page carry, if any (tor.md §6.11): „Anulat” before
 * „Încheiat”, since a cancelled event whose date has passed did not take place, it was called off.
 */
export function eventMarker(event: Pick<EventItem, 'status' | 'past'>): EventMarker | null {
  if (event.status === PUBLIC_EVENT_STATUS.cancelled) return EVENT_MARKER.cancelled;
  return event.past ? EVENT_MARKER.past : null;
}

export const EVENT_MARKER_LABEL: Record<EventMarker, PublicEventsKey> = {
  cancelled: 'cancelledBadge',
  past: 'pastBadge',
};

/**
 * The tag each marker wears, named as MUD's variant rather than as a tone.
 *
 * A marker is not a workflow state code and never was: "Anulat" is a flag on the event and
 * "Încheiat" is a date in the past, so there is nothing for statusTagVariant to classify. The
 * colours are unchanged — a cancelled event is the one thing here a reader must not miss, and a
 * finished one is simply over.
 */
export const EVENT_MARKER_VARIANT: Record<EventMarker, StatusTagVariant> = {
  cancelled: 'danger',
  past: 'neutral',
};
